import { useEffect } from 'react';
import logger from 'lc-logger';
import { Grid } from '@ui-propulsion/react-components';
import Link from 'next/link';
import parse from 'html-react-parser';
import appConfig from 'lc-app-config';
import GlossaryNav from '../../components/glossaryNav/glossaryNav';
import csrf from '../../services/helpers/csrf';
import render404Page from '../../services/helpers/render404Page';
import GlossaryService from '../../services/glossary';
import styles from '../../styles/main.module.scss';
import glossaryStyles from '../../styles/glossary.module.scss';
import GlossaryTerms from '../../components/glossaryTerms/glossaryTerms';
import LcResources from '../../components/lcResources/lcResources';
import trackHeapEvent from '../../services/helpers/trackHeapEvent';
import { getOptimizelyUserExperiments } from '../../services/helpers/common';

export default function Page({
  nav,
  termsOfTheDay,
  latestTerms,
  termsList,
  lcResources,
  experiments,
}) {
  useEffect(() => {
    trackHeapEvent(
      {
        Title: 'Page Load',
        Page: 'Glossary Letter page',
      },
      `CMS_Unification`,
      experiments
    );
  }, [experiments]);
  return (
    <main className={`${styles.main} ${glossaryStyles.main}`}>
      <GlossaryNav nav={nav} />
      <div className={glossaryStyles.bgGrey}>
        <div className={styles.container}>
          <div className={`${styles.section} ${glossaryStyles.section}`}>
            <div className={glossaryStyles.title}>
              <h1>Glossary</h1>
            </div>
            <Grid container>
              {termsOfTheDay && (
                <Grid
                  item
                  xs={12}
                  sm={8}
                  md={9}
                  className={glossaryStyles.termsOfTheDayWrapper}
                >
                  <div className={glossaryStyles.subTitle}>Term of the day</div>
                  <div className={glossaryStyles.termsOfTheDay}>
                    <Link href={termsOfTheDay.path}>
                      <h2>{termsOfTheDay.title}</h2>
                    </Link>
                    <div>{parse(termsOfTheDay.description)}</div>
                  </div>
                </Grid>
              )}
              {latestTerms.length > 0 && (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={3}
                  className={glossaryStyles.latestTermsWrapper}
                >
                  <div className={glossaryStyles.subTitle}>
                    Recently added terms
                  </div>
                  <div className={glossaryStyles.latestTerms}>
                    {latestTerms?.map((item) => {
                      return (
                        <div key={item.title}>
                          <Link href={item.path}>
                            <span>{item.title}</span>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
        </div>
      </div>
      {termsList && (
        <div className={styles.container}>
          <div className={`${styles.section} ${glossaryStyles.section}`}>
            <Grid container className={glossaryStyles.cardGrid}>
              {termsList.map((item) => (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  key={item.title}
                  className={glossaryStyles.cardGridItem}
                >
                  <div className={glossaryStyles.card}>
                    <GlossaryTerms {...item} isGrid />
                  </div>
                </Grid>
              ))}
            </Grid>
          </div>
        </div>
      )}
      {lcResources && (
        <div
          className={`${styles.container} ${glossaryStyles.lcResourcesContainer}`}
          id="Resources"
        >
          <div className={`${styles.section} ${glossaryStyles.section}`}>
            <LcResources title="You May Also Like" lcResources={lcResources} />
          </div>
        </div>
      )}
    </main>
  );
}

export async function getServerSideProps(context) {
  const hub = `Glossary`;
  const userVariation = getOptimizelyUserExperiments(context.req.headers);

  const glossaryService = new GlossaryService(context.preview);
  const props = await glossaryService.getHomePage(hub, userVariation);
  if (!props) {
    logger.info(
      {
        statusCode: 404,
        hub,
      },
      `LC-WEBSITE: Hub home page not found`
    );
    return render404Page(context);
  }
  logger.info(
    {
      statusCode: 200,
      hub,
    },
    `LC-WEBSITE: Generating home page for Hub`
  );

  const hubProps = await glossaryService.getGlossaryHubPage();

  const spaceId = appConfig.get('CONTENTFUL_SPACE_ID');
  const csrfToken = await csrf(context.req, context.res);
  return {
    props: {
      ...props,
      ...hubProps,
      isPreview: !!context.preview,
      spaceId,
      csrfToken,
      experiments: Object.fromEntries(userVariation),
    },
  };
}
